import { useDisclosure } from '@chakra-ui/react';
import { ApiKeyStatusEnum, IApiKey } from '@monetize/types/app';
import { DEFAULT_PAGER, TDataTablePager } from '@monetize/types/common';
import {
  AppColumnDef,
  createColumnHelper,
} from '../../../../components/Monetize/DataTableNew/appTable';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useGetTenantApiKeys,
  useRevokeTenantApiKey,
} from '../../../../api/settingsService';
import {
  MBox,
  MButton,
  MFlex,
  MPageContainer,
  MSettingsPageHeader,
  MText,
} from '../../../../components/Monetize';
import DataTableActions, {
  ActionType,
} from '../../../../components/Monetize/DataTable/MDataTableActions';
import { DataTable } from '../../../../components/Monetize/DataTableNew/DataTable';
import { DataTableRef } from '../../../../components/Monetize/DataTableNew/DataTableTypes';
import {
  ACTION_COLUMN_KEY,
  createActionColumn,
} from '../../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import {
  dateTimeBodyTemplate,
  enumDisplayBodyTemplate,
  getColHeaderWithSize,
  textBodyTemplate,
} from '../../../../components/Monetize/DataTableNew/tableUtilsNew';
import { API_KEY_STATUS_DISPLAY } from '../../../../constants/apiKeys';
import { useSettingsReadOnlyRule } from '../../../../hooks/useSettingsReadOnlyRule';
import { useACL } from '../../../../services/acl/acl';
import { useDocumentHead } from '../../../../services/documentHead';
import { useToast } from '../../../../services/toast';
import APIKeyForm from './APIKeyForm';
import { ApiKeyModal } from './ApiKeyModal';

const columnHelper = createColumnHelper<IApiKey>();

const getColumns = (
  actionBodyTemplate: (rowData: IApiKey) => React.ReactNode,
): AppColumnDef<IApiKey, any>[] => [
  columnHelper.accessor('name', {
    ...getColHeaderWithSize('Name', 250),
    cell: textBodyTemplate(),
    enableSorting: false,
  }),
  columnHelper.accessor('status', {
    ...getColHeaderWithSize('Status', 150),
    cell: enumDisplayBodyTemplate({
      displayMap: API_KEY_STATUS_DISPLAY,
    }),
    enableSorting: false,
  }),
  columnHelper.accessor('createdAt', {
    ...getColHeaderWithSize('Created', 180),
    cell: dateTimeBodyTemplate(),
    enableSorting: false,
  }),
  createActionColumn<IApiKey>(actionBodyTemplate),
];

export const ApiKeysPage = () => {
  const { setDocTitle } = useDocumentHead();
  const { canDo } = useACL();
  const tableRef = useRef<DataTableRef>(null);
  const [addedNewApiKey, setAddedNewApiKey] = useState<string>('');
  const [pager, setPager] = useState<TDataTablePager>(DEFAULT_PAGER);

  const [globalFilter, setGlobalFilter] = useState<string>('');
  const { addToast } = useToast();
  const { mutateAsync: doRevokeTenantApiKey } = useRevokeTenantApiKey();

  const {
    open: isApiKeyModalOpen,
    onOpen: onOpenApiKeyModal,
    onClose: onCloseApiKeyModal,
  } = useDisclosure();

  const {
    open: isViewApiKeyModalOpen,
    onOpen: onOpenViewApiKeyModal,
    onClose: onCloseViewApiKeyModal,
  } = useDisclosure();

  const { data: tenantApiKeys, isLoading } = useGetTenantApiKeys({
    config: pager,
    filters: {
      name: { contains: globalFilter },
    },
    options: {},
  });

  const doDeactivateAPIKey = useCallback(
    async (apiKeyId: string) => {
      await doRevokeTenantApiKey(apiKeyId);
      addToast({
        summary: 'API Key deactivated',
        detail: `You have successfully deactivated the API key `,
        severity: 'success',
      });
    },
    [doRevokeTenantApiKey, addToast],
  );
  const { isReadOnly } = useSettingsReadOnlyRule();
  const actionBodyTemplate = useCallback(
    (rowData: IApiKey) => {
      if (isReadOnly) {
        return null;
      }

      const actions: ActionType[] = [
        {
          title: 'Deactivate',
          enabled: rowData.status === ApiKeyStatusEnum.ACTIVE,
          color: 'danger',
          confirmProps: {
            title: `Are you sure you want to deactivate this API key?`,
            description:
              'Any integrations using this API key will no longer work.',
          },
          action: () => doDeactivateAPIKey(rowData.id),
        },
      ];

      return <DataTableActions actions={actions} />;
    },
    [isReadOnly, doDeactivateAPIKey],
  );

  const columns = useMemo(
    () => getColumns(actionBodyTemplate),
    [actionBodyTemplate],
  );

  useEffect(() => {
    setDocTitle('Settings', 'API Keys');
  }, []);

  return (
    <MPageContainer data-testid="tenant-container" alignItems="stretch">
      <MSettingsPageHeader title="API Keys">
        {tenantApiKeys?.apiKeys.length !== 0 && !isLoading && !isReadOnly && (
          <MFlex>
            <MButton
              variant="primary"
              ml="2"
              onClick={onOpenApiKeyModal}
              data-testid="invite-users-btn"
            >
              New API Key
            </MButton>
          </MFlex>
        )}
      </MSettingsPageHeader>

      <MText mb={6} color="tGray.darkGrayPurple">
        API keys allow you to integrate with MonetizeNow and build rich
        integrations. Refer to the API documentation.
      </MText>

      <MBox width="100%">
        <DataTable
          ref={tableRef}
          columns={columns}
          data={tenantApiKeys?.apiKeys ?? []}
          isLoading={isLoading}
          pinnedRightColumns={[ACTION_COLUMN_KEY]}
          enablePagination={false}
          skipFilterValuePreprocessing
          emptyProps={{
            mainMessage: 'Looks like there are no API Keys added yet.',
            btnLabel: canDo([['apiKeys', 'create']]) ? 'New API Key' : '',
            onClick: onOpenApiKeyModal,
          }}
        />
      </MBox>

      <APIKeyForm
        isOpen={isApiKeyModalOpen}
        showApiKeyModal={(apiKey) => {
          setAddedNewApiKey(apiKey);
          onOpenViewApiKeyModal();
        }}
        onClose={() => {
          onCloseApiKeyModal();
        }}
      />

      <ApiKeyModal
        isOpen={isViewApiKeyModalOpen}
        onClose={() => {
          setAddedNewApiKey('');
          onCloseViewApiKeyModal();
        }}
        apiKey={addedNewApiKey}
      />
    </MPageContainer>
  );
};
